import { motion } from "framer-motion";
import { Crown, TrendingUp } from "lucide-react";
import usePoll, { fmtUsd, timeAgo } from "../hooks/usePoll";
import { EASE } from "./Reveal";

export const MC_TARGETS = [5_000, 10_000, 25_000, 50_000, 100_000, 250_000, 500_000, 1_000_000, 43_000_000];

export const mcLabel = (n) => (n >= 1_000_000 ? `$${n / 1_000_000}M` : `$${n / 1000}K`);

export function milestoneState(mc) {
    if (mc == null) return { reached: 0, next: MC_TARGETS[0], prev: 0, pct: 0 };
    const reached = MC_TARGETS.filter((t) => mc >= t).length;
    const next = MC_TARGETS[reached] ?? null;
    const prev = reached > 0 ? MC_TARGETS[reached - 1] : 0;
    const pct = next ? Math.min(100, Math.max(0, ((mc - prev) / (next - prev)) * 100)) : 100;
    return { reached, next, prev, pct };
}

export default function MilestoneProgress({ stats }) {
    const mc = stats?.marketCap;
    const { reached, next, pct } = milestoneState(mc);
    const label = next ? mcLabel(next) : "$43M";

    return (
        <div data-testid="milestone-progress" className="glass relative mx-auto mt-14 max-w-4xl overflow-hidden rounded-2xl border-yellow-400/25 p-6 md:p-8">
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_70%_60%_at_50%_120%,rgba(245,197,24,0.12),transparent)]" />
            <div className="relative flex flex-col gap-5">
                <div className="flex flex-wrap items-end justify-between gap-3">
                    <div>
                        <div className="flex items-center gap-2 font-mono text-[10px] font-bold uppercase tracking-[0.3em] text-yellow-300">
                            <TrendingUp className="h-4 w-4" /> LIVE MARKET CAP
                        </div>
                        <div data-testid="milestone-progress-mc" className="headline mt-2 text-4xl text-white md:text-5xl">
                            {mc != null ? fmtUsd(mc, { maximumFractionDigits: 0 }) : "—"}
                        </div>
                    </div>
                    <div className="text-right">
                        <div className="kicker !text-[10px] !text-yellow-300/80">{next ? "NEXT MILESTONE" : "END STATION"}</div>
                        <div data-testid="milestone-progress-next" className="headline mt-1 flex items-center justify-end gap-2 text-3xl text-gradient-gold md:text-4xl">
                            {!next && <Crown className="h-7 w-7 text-yellow-300" />}
                            {label} MC
                        </div>
                    </div>
                </div>

                <div className="relative h-4 w-full overflow-hidden rounded-full border border-yellow-400/20 bg-black/40">
                    <motion.div
                        data-testid="milestone-progress-bar"
                        className="h-full rounded-full bg-gradient-to-r from-yellow-600 via-yellow-300 to-yellow-100 shadow-[0_0_24px_rgba(245,197,24,0.55)]"
                        initial={{ width: 0 }}
                        animate={{ width: `${mc != null ? pct : 0}%` }}
                        transition={{ duration: 1.4, ease: EASE }}
                    />
                    <span className="pointer-events-none absolute inset-0 animate-shimmer bg-gradient-to-r from-transparent via-white/25 to-transparent" />
                </div>

                <div className="flex flex-wrap items-center justify-between gap-2 font-mono text-[11px] uppercase tracking-widest text-slate-400">
                    <span data-testid="milestone-progress-pct">
                        {mc == null ? "CONNECTING…" : next ? `${pct.toFixed(1)}% OF THE WAY TO ${label}` : "🚉 END STATION REACHED — AND WE KEEP BUILDING"}
                    </span>
                    <span data-testid="milestone-progress-reached">
                        {reached}/{MC_TARGETS.length} MILESTONES REACHED
                        {stats?.updatedAt ? ` · ${timeAgo(stats.updatedAt).toUpperCase()}` : ""}
                    </span>
                </div>
            </div>
        </div>
    );
}

export function useMarketCap() {
    return usePoll("/stats", 60000);
}
